import { AimOutlined, SwapOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Divider,
  Form,
  Space,
  Tooltip,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteMutations } from '@/hooks/useRouteMutations';
import {
  useRouteWorkspace,
  type RouteDraftEndpoint,
} from '@/stores/RouteWorkspaceContext';
import { useMapTool } from '@/stores/MapToolContext';
import { useTileRevision } from '@/stores/TileRevisionContext';
import type { RouteRequest } from '@/types/routes';
import { extractAxiosMessage } from '@/utils/axiosError';

import AddressAutoComplete from './AddressAutoComplete';
import EndpointBadge from './EndpointBadge';

export default function RouteCreateForm() {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const {
    draft,
    setOrigin,
    setDestination,
    clearOrigin,
    clearDestination,
    cancelDraft,
    selectRoute,
  } = useRouteWorkspace();
  const { mode, setMode } = useMapTool();
  const { bumpRoutes } = useTileRevision();
  const { saving, create } = useRouteMutations();

  const [originText, setOriginText] = useState(
    draft?.origin?.address ?? '',
  );
  const [destText, setDestText] = useState(
    draft?.destination?.address ?? '',
  );

  // Keep the visible input text in sync with the draft endpoint. Matters
  // when origin/destination are set from outside this form — e.g.
  // "Pick on map" (AppLayout's handleMapClick reverse-geocodes the
  // snapped point and calls setOrigin/setDestination) or the industrial
  // reference card. Without this, coordinates would land in the draft
  // but the AutoComplete stays empty, leaving the user to wonder why.
  const originAddress = draft?.origin?.address ?? '';
  const destAddress = draft?.destination?.address ?? '';
  useEffect(() => {
    setOriginText(originAddress);
  }, [originAddress]);
  useEffect(() => {
    setDestText(destAddress);
  }, [destAddress]);

  if (!draft) {
    return null;
  }

  const handleOriginAddressChange = (text: string) => setOriginText(text);
  const handleDestAddressChange = (text: string) => setDestText(text);

  const handleOriginPicked = (suggestion: { lat: number | null; lon: number | null; label: string }) => {
    if (suggestion.lat === null || suggestion.lon === null) {
      return;
    }
    setOrigin({ lat: suggestion.lat, lon: suggestion.lon, address: suggestion.label });
    setOriginText(suggestion.label);
  };

  const handleDestPicked = (suggestion: { lat: number | null; lon: number | null; label: string }) => {
    if (suggestion.lat === null || suggestion.lon === null) {
      return;
    }
    setDestination({ lat: suggestion.lat, lon: suggestion.lon, address: suggestion.label });
    setDestText(suggestion.label);
  };

  const handlePickOriginOnMap = () => setMode('pick_route_origin');
  const handlePickDestOnMap = () => setMode('pick_route_destination');

  const handleSwap = () => {
    const prevOrigin = draft.origin;
    const prevDestination = draft.destination;
    if (prevOrigin) {
      setDestination(prevOrigin);
    } else {
      clearDestination();
    }
    if (prevDestination) {
      setOrigin(prevDestination);
    } else {
      clearOrigin();
    }
    setOriginText(prevDestination?.address ?? '');
    setDestText(prevOrigin?.address ?? '');
  };

  const canSave = draft.origin !== null && draft.destination !== null;

  const handleSubmit = async () => {
    const origin = draft.origin;
    const destination = draft.destination;
    if (!canSave || origin === null || destination === null) {
      return;
    }
    const body: RouteRequest = toRequest(origin, destination);
    try {
      const created = await create(body);
      cancelDraft();
      bumpRoutes();
      setMode('select');
      selectRoute(created.route_id);
    } catch (error) {
      notification.error({
        message: t('routes.create.saveErrorTitle'),
        description: extractAxiosMessage(error) ?? t('errors.unexpected'),
      });
    }
  };

  const handleCancel = () => {
    cancelDraft();
    setMode('select');
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Form layout="vertical">
        <Form.Item label={t('routes.fields.origin')} required>
          <Space.Compact block>
            <AddressAutoComplete
              value={originText}
              onChange={handleOriginAddressChange}
              onSelect={handleOriginPicked}
              placeholder={t('routes.fields.originPlaceholder')}
            />
            <Tooltip title={t('routes.pickOnMap')}>
              <Button
                icon={<AimOutlined />}
                onClick={handlePickOriginOnMap}
                type={mode === 'pick_route_origin' ? 'primary' : 'default'}
              />
            </Tooltip>
          </Space.Compact>
          <EndpointBadge endpoint={draft.origin} />
        </Form.Item>

        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <Button
            size="small"
            icon={<SwapOutlined />}
            onClick={handleSwap}
            disabled={!draft.origin && !draft.destination}
          >
            {t('routes.swap')}
          </Button>
        </div>

        <Form.Item label={t('routes.fields.destination')} required>
          <Space.Compact block>
            <AddressAutoComplete
              value={destText}
              onChange={handleDestAddressChange}
              onSelect={handleDestPicked}
              placeholder={t('routes.fields.destinationPlaceholder')}
            />
            <Tooltip title={t('routes.pickOnMap')}>
              <Button
                icon={<AimOutlined />}
                onClick={handlePickDestOnMap}
                type={mode === 'pick_route_destination' ? 'primary' : 'default'}
              />
            </Tooltip>
          </Space.Compact>
          <EndpointBadge endpoint={draft.destination} />
        </Form.Item>
      </Form>

      <Divider style={{ margin: '4px 0' }} />

      <Space>
        <Button
          type="primary"
          loading={saving}
          disabled={!canSave}
          onClick={handleSubmit}
        >
          {t('routes.create.build')}
        </Button>
        <Button onClick={handleCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>
      </Space>
    </Space>
  );
}

function toRequest(
  origin: RouteDraftEndpoint,
  destination: RouteDraftEndpoint,
): RouteRequest {
  return {
    waypoints: [
      { lat: origin.lat, lon: origin.lon },
      { lat: destination.lat, lon: destination.lon },
    ],
    from_address: origin.address,
    to_address: destination.address,
  };
}
